/**
 * Accept Invite Page
 * Handles team invite acceptance flow
 * URL: /invite/:inviteId?team=teamId
 *
 * Supports both:
 * - Email link sign-in (Firebase Auth email link)
 * - Manual link (user copies link and opens it)
 *
 * Uses Cloud Function for server-validated invite acceptance.
 */

import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { getIdToken } from 'firebase/auth';
import { functions, auth } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle, UserPlus, AlertTriangle, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { mapCfError, isCfUnauthenticated } from '@/lib/authz/cfErrorMapping';

type InviteStatus =
  | 'loading'
  | 'valid'
  | 'expired'
  | 'used'
  | 'revoked'
  | 'not_found'
  | 'error'
  | 'email_mismatch';

interface InviteData {
  email: string;
  maskedEmail?: string;
  invitedBy: string;
  /** V1.2 canonical role field */
  invitedTeamRole: string;
  /** @deprecated kept for display fallback only */
  role: string;
  status: string;
  teamName?: string;
  teamId?: string;
  expiresAt?: Date;
}

// Dev-only logging
const devLog = (...args: unknown[]) => {
  if (import.meta.env.DEV) {
    logger.log('[AcceptInvitePage]', ...args);
  }
};

// Session storage keys for invite flow
const INVITE_STORAGE_KEY = 'pendingInvite';
// FIX L3: Storage key for welcome experience after accepting invite
const INVITE_WELCOME_KEY = 'inviteWelcome';

interface PendingInvite {
  inviteId: string;
  teamId: string;
  email: string;
}

/**
 * Store invite info in sessionStorage for use after signup/login
 */
function storePendingInvite(invite: PendingInvite): void {
  try {
    sessionStorage.setItem(INVITE_STORAGE_KEY, JSON.stringify(invite));
    devLog('Stored pending invite:', invite);
  } catch (e) {
    devLog('Failed to store pending invite:', e);
  }
}

/**
 * Get pending invite from sessionStorage
 */
export function getPendingInvite(): PendingInvite | null {
  try {
    const stored = sessionStorage.getItem(INVITE_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as PendingInvite;
    }
  } catch (e) {
    devLog('Failed to get pending invite:', e);
  }
  return null;
}

/**
 * Clear pending invite from sessionStorage
 */
export function clearPendingInvite(): void {
  try {
    sessionStorage.removeItem(INVITE_STORAGE_KEY);
    devLog('Cleared pending invite');
  } catch (e) {
    devLog('Failed to clear pending invite:', e);
  }
}

/**
 * FIX L3: Store welcome info for invited users
 * This is used to show a welcome experience on the dashboard
 */
export interface InviteWelcomeInfo {
  teamName: string;
  role: string;
  invitedBy: string;
}

export function storeInviteWelcome(info: InviteWelcomeInfo): void {
  try {
    sessionStorage.setItem(INVITE_WELCOME_KEY, JSON.stringify(info));
    devLog('Stored invite welcome info:', info);
  } catch (e) {
    devLog('Failed to store invite welcome:', e);
  }
}

export function getInviteWelcome(): InviteWelcomeInfo | null {
  try {
    const stored = sessionStorage.getItem(INVITE_WELCOME_KEY);
    if (stored) {
      return JSON.parse(stored) as InviteWelcomeInfo;
    }
  } catch (e) {
    devLog('Failed to get invite welcome:', e);
  }
  return null;
}

export function clearInviteWelcome(): void {
  try {
    sessionStorage.removeItem(INVITE_WELCOME_KEY);
    devLog('Cleared invite welcome');
  } catch (e) {
    devLog('Failed to clear invite welcome:', e);
  }
}

export default function AcceptInvitePage() {
  const { t } = useTranslation('onboarding');
  const { inviteId } = useParams<{ inviteId: string }>();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('team');
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  const [inviteStatus, setInviteStatus] = useState<InviteStatus>('loading');
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptSuccess, setAcceptSuccess] = useState(false);

  // Fetch invite data using Cloud Function
  useEffect(() => {
    async function fetchInvite() {
      if (!inviteId) {
        setInviteStatus('not_found');
        return;
      }

      devLog('Fetching invite:', { inviteId, teamId });

      try {
        // Use Cloud Function to check invite status (doesn't require auth)
        const checkInviteStatusFn = httpsCallable<
          { inviteId: string; teamId?: string },
          {
            valid: boolean;
            teamName?: string;
            teamId?: string;
            /** V1.2 canonical role field */
            invitedTeamRole?: string;
            /** @deprecated backward compat */
            role?: string;
            email?: string;
            maskedEmail?: string;
            expiresAt?: string;
            error?: string;
            errorCode?: 'NOT_FOUND' | 'EXPIRED' | 'REVOKED' | 'ALREADY_ACCEPTED';
          }
        >(functions, 'checkInviteStatus');

        const result = await checkInviteStatusFn({ inviteId, teamId: teamId || undefined });
        const data = result.data;

        devLog('checkInviteStatus result:', data);

        if (!data.valid) {
          // Map error codes to invite status
          switch (data.errorCode) {
            case 'EXPIRED':
              setInviteStatus('expired');
              break;
            case 'ALREADY_ACCEPTED':
              setInviteStatus('used');
              break;
            case 'REVOKED':
              setInviteStatus('revoked');
              break;
            case 'NOT_FOUND':
            default:
              setInviteStatus('not_found');
              break;
          }
          return;
        }

        const resolvedRole = data.invitedTeamRole || data.role || 'member';
        const inviteInfo = {
          email: data.email || '',
          maskedEmail: data.maskedEmail,
          invitedBy: '',
          invitedTeamRole: resolvedRole,
          role: resolvedRole,
          status: 'pending',
          teamName: data.teamName || t('invite.defaultTeamName'),
          teamId: data.teamId,
          expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
        };
        setInviteData(inviteInfo);
        setInviteStatus('valid');

        // Store invite info in sessionStorage for signup/login flow
        if (data.teamId && data.email) {
          storePendingInvite({
            inviteId: inviteId,
            teamId: data.teamId,
            email: data.email,
          });
        }
      } catch (error) {
        logger.error('[AcceptInvitePage] Error fetching invite:', error);
        devLog('Error fetching invite:', error);
        setInviteStatus('error');
      }
    }

    void fetchInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteId, teamId]);

  // Handle invite acceptance using Cloud Function
  const handleAcceptInvite = async () => {
    // Use teamId from inviteData if available (more reliable), fallback to URL param
    const effectiveTeamId = inviteData?.teamId || teamId;

    if (!user || !inviteId || !effectiveTeamId) {
      devLog('Cannot accept invite - missing data:', { user: !!user, inviteId, effectiveTeamId });
      return;
    }

    devLog('Accepting invite:', { inviteId, teamId: effectiveTeamId, userEmail: user.email });

    try {
      setIsAccepting(true);

      // Use Cloud Function for server-validated invite acceptance
      const acceptTeamInviteFn = httpsCallable<
        { inviteId: string; teamId: string },
        {
          success: boolean;
          error?: string;
          errorCode?:
            | 'NOT_FOUND'
            | 'EXPIRED'
            | 'REVOKED'
            | 'ALREADY_ACCEPTED'
            | 'EMAIL_MISMATCH'
            | 'ALREADY_MEMBER'
            | 'INVALID_REQUEST';
          teamId?: string;
          /** V1.2 canonical role field */
          invitedTeamRole?: string;
          /** @deprecated backward compat */
          role?: string;
        }
      >(functions, 'acceptTeamInvite');

      const result = await acceptTeamInviteFn({ inviteId, teamId: effectiveTeamId });
      const data = result.data;

      devLog('acceptTeamInvite result:', data);

      if (!data.success) {
        // Handle specific error codes
        switch (data.errorCode) {
          case 'EMAIL_MISMATCH':
            setInviteStatus('email_mismatch');
            toast.error(t('invite.toast.emailMismatch'), {
              description: data.error,
            });
            return;
          case 'EXPIRED':
            setInviteStatus('expired');
            return;
          case 'REVOKED':
            setInviteStatus('revoked');
            return;
          case 'ALREADY_ACCEPTED':
            setInviteStatus('used');
            return;
          case 'ALREADY_MEMBER':
            toast.info(t('invite.toast.alreadyMember'));
            navigate('/dashboard', { replace: true });
            return;
          default:
            throw new Error(data.error || 'Failed to accept invite');
        }
      }

      setAcceptSuccess(true);
      toast.success(t('invite.toast.success'), {
        description: t('invite.toast.successDescription', {
          teamName: inviteData?.teamName || t('invite.defaultTeamName'),
        }),
      });

      // Clear pending invite from sessionStorage
      clearPendingInvite();
      devLog('Cleared pending invite from sessionStorage');

      // FIX L3: Store welcome info for dashboard to show welcome experience
      if (inviteData) {
        storeInviteWelcome({
          teamName: inviteData.teamName || t('invite.defaultTeamName'),
          role: inviteData.invitedTeamRole || inviteData.role,
          invitedBy: inviteData.invitedBy,
        });
        devLog('Stored invite welcome info for dashboard');
      }

      // Force refresh auth token to pick up any new claims
      // This ensures TeamProvider gets fresh auth state
      if (auth.currentUser) {
        try {
          await getIdToken(auth.currentUser, true);
          devLog('Auth token refreshed');
        } catch (tokenError) {
          devLog('Token refresh failed (non-critical):', tokenError);
        }
      }

      // Small delay to allow Firestore listeners to pick up the new profile
      // Then navigate to dashboard
      devLog('Navigating to dashboard in 1.5s...');
      setTimeout(() => {
        // Use window.location for a full page reload to ensure clean state
        // This guarantees TeamProvider reinitializes with the new activeTeamId
        window.location.href = '/dashboard';
      }, 1500);
    } catch (error) {
      logger.error('[AcceptInvitePage] Error accepting invite:', error);
      devLog('Error accepting invite:', error);
      if (isCfUnauthenticated(error)) {
        navigate(`/login?redirect=${encodeURIComponent(`/invite/${inviteId}?team=${teamId}`)}`);
        return;
      }
      const errorMessage = mapCfError(error) ?? t('invite.toast.errorDescription');
      toast.error(t('invite.toast.error'), {
        description: errorMessage,
      });
    } finally {
      setIsAccepting(false);
    }
  };

  // Loading state
  if (authLoading || inviteStatus === 'loading') {
    return (
      <AuthLayout>
        <Card className="rounded-2xl shadow-lg border-border max-w-md w-full">
          <CardContent className="pt-10 pb-10 px-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-4" />
            <p className="text-muted-foreground">{t('invite.loading')}</p>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  // Not found
  if (inviteStatus === 'not_found') {
    return (
      <AuthLayout>
        <Card className="rounded-2xl shadow-lg border-border max-w-md w-full">
          <CardContent className="pt-10 pb-10 px-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-6">
              <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-xl font-semibold mb-2">{t('invite.notFound.title')}</h1>
            <p className="text-muted-foreground mb-6">{t('invite.notFound.description')}</p>
            <Button asChild>
              <Link to="/login">{t('invite.notFound.action')}</Link>
            </Button>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  // Expired
  if (inviteStatus === 'expired') {
    return (
      <AuthLayout>
        <Card className="rounded-2xl shadow-lg border-border max-w-md w-full">
          <CardContent className="pt-10 pb-10 px-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-6">
              <AlertTriangle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h1 className="text-xl font-semibold mb-2">{t('invite.expired.title')}</h1>
            <p className="text-muted-foreground mb-6">{t('invite.expired.description')}</p>
            <Button asChild>
              <Link to="/login">{t('invite.expired.action')}</Link>
            </Button>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  // Already used
  if (inviteStatus === 'used') {
    return (
      <AuthLayout>
        <Card className="rounded-2xl shadow-lg border-border max-w-md w-full">
          <CardContent className="pt-10 pb-10 px-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-6">
              <CheckCircle2 className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-xl font-semibold mb-2">{t('invite.used.title')}</h1>
            <p className="text-muted-foreground mb-6">{t('invite.used.description')}</p>
            <Button asChild>
              <Link to="/login">{t('invite.used.action')}</Link>
            </Button>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  // Revoked
  if (inviteStatus === 'revoked') {
    return (
      <AuthLayout>
        <Card className="rounded-2xl shadow-lg border-border max-w-md w-full">
          <CardContent className="pt-10 pb-10 px-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-6">
              <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-xl font-semibold mb-2">{t('invite.revoked.title')}</h1>
            <p className="text-muted-foreground mb-6">{t('invite.revoked.description')}</p>
            <Button asChild>
              <Link to="/login">{t('invite.revoked.action')}</Link>
            </Button>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  // Email mismatch - logged in with wrong email
  if (inviteStatus === 'email_mismatch') {
    return (
      <AuthLayout>
        <Card className="rounded-2xl shadow-lg border-border max-w-md w-full">
          <CardContent className="pt-10 pb-10 px-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-6">
              <AlertTriangle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h1 className="text-xl font-semibold mb-2">{t('invite.emailMismatch.title')}</h1>
            <p className="text-muted-foreground mb-4">
              {t('invite.emailMismatch.description', {
                email: inviteData?.maskedEmail || inviteData?.email,
              })}
            </p>
            <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm">
                <span className="text-muted-foreground">
                  {t('invite.authenticated.loggedInAs')}
                </span>{' '}
                <strong>{user?.email}</strong>
              </p>
            </div>
            <div className="space-y-3">
              <Button asChild variant="outline" className="w-full">
                <Link to={`/login?redirect=/invite/${inviteId}?team=${teamId}`}>
                  <LogIn className="h-4 w-4 mr-2" />
                  {t('invite.emailMismatch.switchAccount')}
                </Link>
              </Button>
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="w-full">
                {t('invite.actions.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  // Error
  if (inviteStatus === 'error') {
    return (
      <AuthLayout>
        <Card className="rounded-2xl shadow-lg border-border max-w-md w-full">
          <CardContent className="pt-10 pb-10 px-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-6">
              <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-xl font-semibold mb-2">{t('invite.error.title')}</h1>
            <p className="text-muted-foreground mb-6">{t('invite.error.description')}</p>
            <Button onClick={() => window.location.reload()}>{t('invite.error.action')}</Button>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  // Success state
  if (acceptSuccess) {
    return (
      <AuthLayout>
        <Card className="rounded-2xl shadow-lg border-border max-w-md w-full">
          <CardContent className="pt-10 pb-10 px-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-6">
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h1 className="text-xl font-semibold mb-2">
              {t('invite.success.title', { teamName: inviteData?.teamName })}
            </h1>
            <p className="text-muted-foreground mb-4">{t('invite.success.description')}</p>
            <Loader2 className="h-5 w-5 animate-spin mx-auto text-primary" />
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  // Valid invite - not logged in
  if (!isAuthenticated) {
    return (
      <AuthLayout>
        <Card className="rounded-2xl shadow-lg border-border max-w-md w-full">
          <CardContent className="pt-10 pb-10 px-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
              <UserPlus className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-xl font-semibold mb-2">
              {t('invite.notAuthenticated.title', { teamName: inviteData?.teamName })}
            </h1>
            <p className="text-muted-foreground mb-6">{t('invite.notAuthenticated.description')}</p>

            <div className="space-y-3">
              <Button asChild className="w-full">
                <Link
                  to={`/signup?inviteId=${inviteId}&team=${inviteData?.teamId || teamId}&email=${encodeURIComponent(inviteData?.email || '')}`}
                >
                  {t('invite.notAuthenticated.createAccount')}
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link
                  to={`/login?redirect=${encodeURIComponent(`/invite/${inviteId}?team=${inviteData?.teamId || teamId}`)}`}
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  {t('invite.notAuthenticated.login')}
                </Link>
              </Button>
            </div>

            <p className="text-xs text-muted-foreground mt-6">
              {t('invite.notAuthenticated.inviteFor')} {inviteData?.email}
            </p>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  // Valid invite - logged in
  // Map canonical invitedTeamRole to localized label
  const resolvedRole = inviteData?.invitedTeamRole || inviteData?.role || 'member';
  const roleLabel = (() => {
    switch (resolvedRole) {
      case 'owner':
        return t('invite.authenticated.roleOwner');
      case 'admin':
        return t('invite.authenticated.roleAdmin');
      case 'manager':
        return t('invite.authenticated.roleManager');
      default:
        return t('invite.authenticated.roleMember');
    }
  })();

  return (
    <AuthLayout>
      <Card className="rounded-2xl shadow-lg border-border max-w-md w-full">
        <CardContent className="pt-10 pb-10 px-8 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
            <UserPlus className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-xl font-semibold mb-2">
            {t('invite.authenticated.title', { teamName: inviteData?.teamName })}
          </h1>
          <p className="text-muted-foreground mb-6">
            <Trans
              i18nKey="invite.authenticated.description"
              ns="onboarding"
              values={{ role: roleLabel }}
              components={{ strong: <strong /> }}
            />
          </p>

          <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm">
              <span className="text-muted-foreground">{t('invite.authenticated.loggedInAs')}</span>{' '}
              <strong>{user?.email}</strong>
            </p>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => void handleAcceptInvite()}
              disabled={isAccepting}
              className="w-full"
            >
              {isAccepting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('invite.actions.accepting')}
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {t('invite.actions.accept')}
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              disabled={isAccepting}
              className="w-full"
            >
              {t('invite.actions.cancel')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
